/**
 * AntarDristi — Local DB layer
 *
 * Drop-in replacement for direct localStorage calls. Keeps the synchronous
 * API everything in the app already uses, but mirrors every write to
 * IndexedDB for durable, larger-capacity storage.
 *
 * Why this matters
 * ────────────────
 * - localStorage caps at roughly 5–10 MB per origin and gets cleared by
 *   some browser-cleanup paths that DON'T touch IndexedDB.
 * - IndexedDB happily holds tens of MB to GBs and survives more cleanup
 *   passes — exactly what we want for long-running conversations and
 *   accumulating life-context graphs.
 *
 * On app boot, `restoreFromIdb()` copies anything in IndexedDB that's
 * missing from localStorage back into localStorage, so a session that
 * starts with an empty localStorage (e.g. after a privacy clear) still
 * has all prior chats / graphs / snapshots ready by the next render.
 *
 * Privacy contract is unchanged: data only ever leaves the browser when
 * the user explicitly asks Gemini a question.
 */

const DB_NAME = 'antardristi';
const DB_VERSION = 1;
const STORE = 'kv';

// All app keys that we mirror to IndexedDB. Listed as exact keys OR prefixes
// (anything starting with `prefix` matches). Keep this tight so we don't
// mirror unrelated keys some other library might write.
const MIRROR_PREFIXES = [
  'antardristi_charts',
  'antardristi_active_profile',
  'antardristi_gemini_keys',
  'antardristi_gemini_key',          // legacy single-key
  'antardristi_insights_',
  'antardristi_big_picture',         // matches both the legacy global key
                                     // and the per-chart `_<name>` buckets
  'antardristi_bp_chat_',
  'antardristi_dashboard_',
];

function shouldMirror(key) {
  return MIRROR_PREFIXES.some((p) => key === p || key.startsWith(p));
}

// ── IndexedDB helpers ─────────────────────────────────────────────

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    dbPromise = Promise.reject(new Error('IndexedDB unavailable'));
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch (err) { reject(err); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  return dbPromise;
}

function idbSet(key, value) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function idbGet(key) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function idbDelete(key) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function idbListKeys() {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  }));
}

// Serialize writes so we don't fire dozens of overlapping transactions on
// rapid typing. Errors are swallowed — IndexedDB is best-effort durability;
// localStorage is always the source of truth for the current session.
let writeQueue = Promise.resolve();
function queueWrite(fn) {
  writeQueue = writeQueue.then(fn).catch(() => {});
}

// ── Public, sync, drop-in API ─────────────────────────────────────

export function setItem(key, value) {
  try { localStorage.setItem(key, value); } catch {}
  if (shouldMirror(key)) queueWrite(() => idbSet(key, value));
}

export function getItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function removeItem(key) {
  try { localStorage.removeItem(key); } catch {}
  if (shouldMirror(key)) queueWrite(() => idbDelete(key));
}

// ── Boot-time restore from IndexedDB ──────────────────────────────

let restorePromise = null;
let restoreDone = false;

/**
 * On app boot, repopulate localStorage from IndexedDB for any mirrored
 * keys that are missing locally. Idempotent. Returns a Promise so callers
 * that care can await the first paint with restored data.
 */
export function restoreFromIdb() {
  if (restoreDone) return Promise.resolve();
  if (restorePromise) return restorePromise;
  restorePromise = (async () => {
    try {
      const keys = await idbListKeys();
      for (const k of keys) {
        if (typeof k !== 'string') continue;
        let inLocal;
        try { inLocal = localStorage.getItem(k); } catch { inLocal = null; }
        if (typeof inLocal === 'string') continue; // localStorage already has it
        const v = await idbGet(k);
        if (typeof v === 'string') {
          try { localStorage.setItem(k, v); } catch {}
        }
      }
    } catch {
      /* IndexedDB unavailable — fall back to localStorage-only mode */
    }
    restoreDone = true;
  })();
  return restorePromise;
}

/**
 * Test/debug utility — wipe both layers of a key. Not part of the
 * public app surface; consumers normally use removeItem.
 */
export async function purgeKey(key) {
  removeItem(key);
  try { await idbDelete(key); } catch {}
}
